import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtEuro, MONTHS_FR } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { CategoryCombobox } from "@/components/CategoryCombobox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UploadCloud, FileSpreadsheet, Sparkles, Check, Loader2, Trash2, X, AlertCircle, CalendarClock } from "lucide-react";
import { StatementBanner } from "@/components/StatementBanner";
import { toast } from "sonner";
import { motion } from "framer-motion";

let DOC_SEQ = 0;

export default function ImportPage() {
  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [hideImported, setHideImported] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [savingsAccounts, setSavingsAccounts] = useState([]);
  const [plannedItems, setPlannedItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projCache, setProjCache] = useState({});
  const inputRef = useRef();

  const loadProj = async (id) => {
    if (!id || projCache[id]) return;
    try {
      const { data } = await api.get(`/projects/${id}`);
      const textCol = (data.columns || []).find((c) => c.type === "text");
      const rows = (data.rows || []).filter((r) => (r.kind || "data") === "data").map((r) => ({
        id: r.id, label: r.label || (textCol && (r.cells || {})[textCol.id]) || "Ligne",
      }));
      const payerCol = (data.columns || []).find((c) => c.id === data.payer_column_id);
      const payers = (payerCol && payerCol.options && payerCol.options.length) ? payerCol.options : ["Moi", "Ma société"];
      setProjCache((c) => ({ ...c, [id]: { rows, payers, category_id: data.category_id || "", payer_me: data.payer_me_value || "Moi" } }));
    } catch { /* ignore */ }
  };

  const loadAccounts = () => {
    api.get("/bank-accounts").then((r) => setBankAccounts(r.data));
    api.get("/savings/accounts").then((r) => setSavingsAccounts(r.data));
  };
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    loadAccounts();
    const y = new Date().getFullYear();
    Promise.all([y - 1, y, y + 1].map((yr) => api.get(`/planned?year=${yr}`).then((r) => r.data).catch(() => [])))
      .then((lists) => setPlannedItems([].concat(...lists)));
    // Restaure les imports en cours / non traités (le serveur analyse en tâche de fond).
    api.get("/import/jobs").then(({ data }) => {
      if (!data || data.length === 0) return;
      const restored = data.map((job) => {
        const base = { id: `job-${job.id}`, jobId: job.id, fileName: job.file_name || "fichier", rows: [], error: "", saving: false, statement: job.statement || null };
        if (job.status === "done") return { ...base, status: "done", rows: (job.transactions || []).map((t, i) => ({ ...t, _key: i, _include: !t.duplicate })) };
        if (job.status === "error") return { ...base, status: "error", error: job.error || "Erreur lors de l'analyse" };
        return { ...base, status: "analyzing" };
      });
      setDocs(restored);
      setActive((cur) => cur ?? restored[0].id);
      restored.filter((d) => d.status === "analyzing").forEach((d) => pollJob(d.id, d.jobId));
    }).catch(() => {});
  }, []);

  const updateDoc = (id, patch) =>
    setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, ...(typeof patch === "function" ? patch(d) : patch) } : d)));

  const pollJob = async (id, jobId) => {
    const started = Date.now();
    while (Date.now() - started < 300000) {
      await new Promise((r) => setTimeout(r, 2500));
      let job;
      try { ({ data: job } = await api.get(`/import/status/${jobId}`)); } catch { continue; }
      if (job.status === "done") {
        const rows = (job.transactions || []).map((t, i) => ({ ...t, _key: i, _include: !t.duplicate }));
        updateDoc(id, { status: "done", rows, statement: job.statement || null });
        return;
      }
      if (job.status === "error") { updateDoc(id, { status: "error", error: job.error || "Erreur lors de l'analyse" }); return; }
    }
    updateDoc(id, { status: "error", error: "L'analyse a expiré." });
  };

  const analyzeFile = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateDoc(id, { jobId: data.job_id });
      await pollJob(id, data.job_id);
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de l'analyse du fichier";
      updateDoc(id, { status: "error", error: msg });
      toast.error(`${file.name} : ${msg}`);
    }
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const newDocs = files.map((f) => ({
      id: ++DOC_SEQ, fileName: f.name, status: "analyzing", rows: [], error: "", saving: false,
    }));
    setDocs((ds) => [...ds, ...newDocs]);
    setActive((cur) => cur ?? newDocs[0].id);
    newDocs.forEach((d, i) => analyzeFile(d.id, files[i]));
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

  const updateRow = (docId, key, patch) =>
    updateDoc(docId, (d) => ({ rows: d.rows.map((r) => (r._key === key ? { ...r, ...patch } : r)) }));
  const removeRow = (docId, key) =>
    updateDoc(docId, (d) => ({ rows: d.rows.filter((r) => r._key !== key) }));

  const closeDoc = (id) => {
    const doc = docs.find((d) => d.id === id);
    if (doc?.jobId) api.post(`/import/dismiss/${doc.jobId}`).catch(() => {});
    setDocs((ds) => ds.filter((d) => d.id !== id));
    setActive((cur) => {
      if (cur !== id) return cur;
      const rest = docs.filter((d) => d.id !== id);
      return rest.length ? rest[0].id : null;
    });
  };

  const confirmDoc = async (doc) => {
    const toSave = doc.rows.filter((r) => r._include && (r.category_id || r.project_id || r.type === "transfer"));
    if (toSave.length === 0) { toast.error("Sélectionnez au moins un mouvement avec une catégorie ou un projet."); return; }
    updateDoc(doc.id, { saving: true });
    try {
      await api.post("/import/confirm", {
        transactions: toSave.map((r) => ({
          date: r.date, vendor: r.vendor, description: r.description, amount: r.amount,
          category_id: r.category_id, type: r.type, region: r.region || "", owner: "Commun",
          planned_id: r.project_id ? null : (r.planned_id || null),
          project_id: r.project_id || null, project_row_id: r.project_row_id || null, project_payer: r.project_payer || null,
          source: "import",
        })),
      });
      toast.success(`${doc.fileName} : ${toSave.length} mouvements importés.`);
      closeDoc(doc.id);
    } catch { toast.error("Erreur lors de l'import"); updateDoc(doc.id, { saving: false }); }
  };

  const grouped = categories.reduce((acc, c) => { (acc[c.type] = acc[c.type] || []).push(c); return acc; }, {});
  const activeDoc = docs.find((d) => d.id === active) || null;

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Import PDF & Excel</h1>
        <p className="text-muted-foreground text-sm mt-1">Déposez plusieurs relevés ou factures à la fois. L'analyse se poursuit en arrière-plan : vous pouvez fermer l'onglet et revenir plus tard, vos imports non traités seront toujours là.</p>
      </div>

      <Card
        className={`border-2 border-dashed transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border"} p-10`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="import-dropzone"
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
            <UploadCloud className="w-7 h-7" />
          </div>
          <p className="font-medium">Glissez vos fichiers ici</p>
          <p className="text-sm text-muted-foreground mt-1">Formats acceptés : Excel (.xlsx), CSV, PDF · plusieurs fichiers possibles</p>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.pdf" multiple className="hidden"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="file-dropzone-input" />
          <Button className="mt-4" onClick={() => inputRef.current?.click()} data-testid="btn-import-file">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Choisir des fichiers
          </Button>
        </div>
      </Card>

      {docs.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2" data-testid="import-tabs">
            {docs.map((d) => (
              <button
                key={d.id}
                onClick={() => setActive(d.id)}
                data-testid={`import-tab-${d.id}`}
                className={`group flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border text-sm transition-colors ${
                  active === d.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"
                }`}
              >
                {d.status === "analyzing" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {d.status === "done" && <Sparkles className={`w-3.5 h-3.5 ${active === d.id ? "" : "text-accent"}`} />}
                {d.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                <span className="max-w-[180px] truncate">{d.fileName}</span>
                {d.status === "done" && (
                  <span className={`text-[10px] ${active === d.id ? "opacity-80" : "text-muted-foreground"}`}>
                    {d.rows.filter((r) => r.category_id).length}/{d.rows.length}
                  </span>
                )}
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); closeDoc(d.id); }}
                  data-testid={`import-tab-close-${d.id}`}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
                >
                  <X className="w-3.5 h-3.5" />
                </span>
              </button>
            ))}
          </div>

          {/* Active document panel */}
          {activeDoc && (
            <Card className="border-border overflow-hidden" data-testid="import-active-panel">
              {activeDoc.status === "analyzing" && (
                <div className="p-10 flex flex-col items-center text-center text-muted-foreground">
                  <Loader2 className="w-6 h-6 animate-spin text-primary mb-3" />
                  <p className="text-sm">Analyse de {activeDoc.fileName} en cours…</p>
                </div>
              )}
              {activeDoc.status === "error" && (
                <div className="p-10 flex flex-col items-center text-center">
                  <AlertCircle className="w-6 h-6 text-red-500 mb-3" />
                  <p className="text-sm font-medium">Impossible d'analyser {activeDoc.fileName}</p>
                  <p className="text-xs text-muted-foreground mt-1">{activeDoc.error}</p>
                </div>
              )}
              {activeDoc.status === "done" && (
                <StatementBanner
                  key={activeDoc.id}
                  doc={activeDoc}
                  bankAccounts={bankAccounts}
                  savingsAccounts={savingsAccounts}
                  onApplied={(docId) => { updateDoc(docId, { statementApplied: true }); loadAccounts(); }}
                />
              )}
              {activeDoc.status === "done" && activeDoc.rows.length === 0 && (
                <div className="p-10 text-center text-sm text-muted-foreground">Aucun mouvement détecté dans ce fichier.</div>
              )}
              {activeDoc.status === "done" && activeDoc.rows.length > 0 && (
                <>
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-accent" />
                      <span className="font-medium text-sm">
                        {activeDoc.fileName} — {activeDoc.rows.length} lignes · {activeDoc.rows.filter((r) => r._include && (r.category_id || r.project_id || r.type === "transfer")).length} à importer
                      </span>
                    </div>
                    <Button
                      onClick={() => confirmDoc(activeDoc)}
                      disabled={activeDoc.saving || activeDoc.rows.filter((r) => r._include && (r.category_id || r.project_id || r.type === "transfer")).length === 0}
                      data-testid="confirm-import-button"
                    >
                      {activeDoc.saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Importer {activeDoc.rows.filter((r) => r._include && (r.category_id || r.project_id || r.type === "transfer")).length > 0 ? `(${activeDoc.rows.filter((r) => r._include && (r.category_id || r.project_id || r.type === "transfer")).length})` : ""}
                    </Button>
                  </div>
                  {activeDoc.rows.some((r) => r.duplicate) && (
                    <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border bg-amber-50 text-amber-800" data-testid="duplicate-warning">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <div className="text-sm flex-1">
                        {activeDoc.rows.filter((r) => r.duplicate).length} mouvement(s) semblent <span className="font-semibold">déjà importés</span> (même date et montant). Ils sont décochés par défaut — cochez-les pour les importer quand même.
                      </div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer shrink-0 select-none">
                        <Checkbox checked={hideImported} onCheckedChange={(v) => setHideImported(!!v)} data-testid="hide-imported-toggle" />
                        Masquer les déjà importés
                      </label>
                    </div>
                  )}
                  {activeDoc.rows.some((r) => r.transfer) && (
                    <div className="flex items-start gap-2.5 px-4 py-3 border-b border-border bg-sky-50 text-sky-800" data-testid="transfer-warning">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="text-sm">
                        {activeDoc.rows.filter((r) => r.transfer).length} mouvement(s) sont des <span className="font-semibold">transferts entre comptes</span>. Ils seront importés mais <span className="font-semibold">non comptés comme revenus</span> (pour éviter de compter deux fois une même somme).
                      </div>
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead className="w-28">Date</TableHead>
                        <TableHead>Prestataire</TableHead>
                        <TableHead className="w-24">Type</TableHead>
                        <TableHead className="text-right w-28">Montant</TableHead>
                        <TableHead className="w-64">Catégorie</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeDoc.rows.filter((r) => !(hideImported && r.duplicate)).map((r) => (
                        <TableRow key={r._key} data-testid={`import-row-${r._key}`} className={r.duplicate ? "bg-amber-50/60" : r.transfer ? "bg-sky-50/60" : ""}>
                          <TableCell>
                            <Checkbox checked={!!r._include} onCheckedChange={(v) => updateRow(activeDoc.id, r._key, { _include: !!v })} data-testid={`import-include-${r._key}`} />
                          </TableCell>
                          <TableCell><Input type="date" value={r.date} onChange={(e) => updateRow(activeDoc.id, r._key, { date: e.target.value })} className="h-8 text-xs" /></TableCell>
                          <TableCell>
                            <div className="font-medium text-sm flex items-center gap-1.5">
                              {r.vendor || "—"}
                              {r.duplicate && <Badge variant="outline" className="text-[10px] shrink-0 border-amber-400 text-amber-700">déjà importé</Badge>}
                              {r.transfer && <Badge variant="outline" className="text-[10px] shrink-0 border-sky-400 text-sky-700">transfert</Badge>}
                            </div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">{r.description}{r.region ? ` · ${r.region}` : ""}</div>
                          </TableCell>
                          <TableCell>
                            <Select value={r.type} onValueChange={(v) => updateRow(activeDoc.id, r._key, { type: v, category_id: "" })}>
                              <SelectTrigger className="h-8 text-xs w-[110px]" data-testid={`import-type-select-${r._key}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="expense">Dépense</SelectItem>
                                <SelectItem value="income">Revenu</SelectItem>
                                <SelectItem value="transfer">Transfert</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-num text-sm">{fmtEuro(r.amount)}</TableCell>
                          <TableCell>
                            {!r.project_id && (
                            <div className="flex items-center gap-1.5">
                              <CategoryCombobox
                                categories={grouped[r.type] || []}
                                value={r.category_id || ""}
                                onChange={(v) => updateRow(activeDoc.id, r._key, { category_id: v })}
                                testid={`import-cat-select-${r._key}`}
                              />
                              {r.auto_matched && <Badge variant="outline" className="text-[10px] shrink-0 border-emerald-300 text-emerald-600">auto</Badge>}
                              {r.type !== "transfer" && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className={`h-8 w-8 shrink-0 ${r.planned_id ? "text-amber-600" : "text-muted-foreground"}`} title="Lier à un élément exceptionnel" data-testid={`import-link-planned-${r._key}`}>
                                      <CalendarClock className="w-4 h-4" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72 p-0" align="end">
                                    <div className="px-3 py-2 text-xs font-medium border-b border-border">Lier à {r.type === "income" ? "une rentrée exceptionnelle" : "une dépense exceptionnelle"}</div>
                                    <div className="max-h-56 overflow-auto py-1">
                                      {plannedItems.filter((p) => p.type === r.type).length === 0 ? (
                                        <div className="px-3 py-3 text-xs text-muted-foreground">Aucun élément exceptionnel. Créez-en dans « Vue Annuelle & Prévisions ».</div>
                                      ) : plannedItems.filter((p) => p.type === r.type).map((p) => (
                                        <button key={p.id} onClick={() => updateRow(activeDoc.id, r._key, { planned_id: p.id, planned_label: p.label, category_id: p.category_id || r.category_id })}
                                          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary flex items-center justify-between gap-2 ${r.planned_id === p.id ? "bg-secondary" : ""}`} data-testid={`import-planned-option-${r._key}-${p.id}`}>
                                          <span className="truncate">{p.label}<span className="text-xs text-muted-foreground"> · {MONTHS_FR[p.month - 1]} {p.year}</span></span>
                                          <span className="font-num text-xs shrink-0">{fmtEuro(p.amount)}</span>
                                        </button>
                                      ))}
                                    </div>
                                    {r.planned_id && (
                                      <button onClick={() => updateRow(activeDoc.id, r._key, { planned_id: null, planned_label: null })} className="w-full text-left px-3 py-2 text-xs text-destructive border-t border-border hover:bg-secondary">Délier</button>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                            )}
                            {!r.project_id && r.planned_id && <div className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1" data-testid={`import-planned-badge-${r._key}`}><CalendarClock className="w-3 h-3" />→ {r.planned_label}</div>}
                            {r.project_id && (
                              <div className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`import-project-cat-${r._key}`}>
                                Catégorie définie par le projet{(() => { const cid = projCache[r.project_id]?.category_id; const cn = categories.find((c) => c.id === cid)?.name; return cn ? <span className="font-medium text-foreground">· {cn}</span> : null; })()}
                              </div>
                            )}
                            {r.type !== "transfer" && projects.length > 0 && (
                              <div className="mt-1.5 flex flex-col gap-1">
                                <Select value={r.project_id || "none"} onValueChange={(v) => { if (v === "none") { updateRow(activeDoc.id, r._key, { project_id: null, project_row_id: null, project_payer: null }); } else { loadProj(v); updateRow(activeDoc.id, r._key, { project_id: v, project_row_id: null, project_payer: (projCache[v]?.payer_me || "Moi"), planned_id: null, planned_label: null }); } }}>
                                  <SelectTrigger className="h-7 text-[11px] w-full" data-testid={`import-project-select-${r._key}`}><SelectValue placeholder="Projet (optionnel)" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Aucun projet</SelectItem>
                                    {projects.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                {r.project_id && (
                                  <div className="flex gap-1">
                                    <Select value={r.project_row_id || "none"} onValueChange={(v) => updateRow(activeDoc.id, r._key, { project_row_id: v === "none" ? null : v })}>
                                      <SelectTrigger className="h-7 text-[11px] flex-1" data-testid={`import-project-row-${r._key}`}><SelectValue placeholder="Ligne" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">Choisir la ligne…</SelectItem>
                                        {(projCache[r.project_id]?.rows || []).map((row) => <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                    <Select value={r.project_payer || undefined} onValueChange={(v) => updateRow(activeDoc.id, r._key, { project_payer: v })}>
                                      <SelectTrigger className="h-7 text-[11px] w-[110px]" data-testid={`import-project-payer-${r._key}`}><SelectValue placeholder="Payé par" /></SelectTrigger>
                                      <SelectContent>{(projCache[r.project_id]?.payers || ["Moi", "Ma société"]).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeRow(activeDoc.id, r._key)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Card>
          )}
        </motion.div>
      )}
    </div>
  );
}
